import React, { useState, useRef } from "react";
import { uploadEmployeeDocument } from "../../services/documentService";
import {
  Edit,
  X,
  User,
  Mail,
  Phone,
  Building,
  CheckCircle,
  DollarSign,
  Award,
  MapPin,
  Home,
  Calendar,
  Heart,
  Flag,
  Save,
  Upload,
  FileText,
  Trash2,
} from "../../constants/icons";

const EditEmployeeModal = ({
  selectedEmployee = null,
  setShowEditEmployee = () => {},
  setSelectedEmployee = () => {},
  language = "en",
  text = {},
  unifiedControlStyle = () => ({}),
  departments = [],
  ReusableDropdown = () => null,
  handleUpdateEmployee = async () => {},
  handleDeleteEmployee = async () => {},
  theme = "light",
  screenSize = "md",
  fonts = {},
  spacing = {},
  styles: stylesProp = {},
  loading = false,
  handlePrimaryBtnMouseEnter = () => {},
  handlePrimaryBtnMouseLeave = () => {},
  clientName = "",
}) => {
  const isDark = theme === "dark";

  const defaultStyles = {
    modal: {
      position: "fixed",
      inset: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "rgba(0,0,0,0.5)",
      zIndex: 9999,
      backdropFilter: "blur(3px)",
      animation: "fadeIn 300ms ease-out",
    },
    modalContent: {
      position: "relative",
      width: "92%",
      maxWidth: 1000,
      maxHeight: "92vh",
      overflowY: "auto",
      background: isDark
        ? "linear-gradient(135deg, #0f172a 0%, #1a2a3a 100%)"
        : "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
      color: isDark ? "#e6eef8" : "#0f172a",
      borderRadius: 16,
      padding: "32px",
      boxShadow: isDark
        ? "0 25px 60px rgba(0,0,0,0.8), 0 0 1px rgba(255,255,255,0.1)"
        : "0 20px 50px rgba(0,0,0,0.12), 0 0 1px rgba(0,0,0,0.04)",
      border: isDark
        ? "1px solid rgba(255,255,255,0.1)"
        : "1px solid rgba(148,163,184,0.15)",
      scrollbarWidth: "none",
      msOverflowStyle: "none",
      animation: "slideUp 400ms cubic-bezier(0.34, 1.56, 0.64, 1)",
    },
    modalHeader: {
      marginBottom: 32,
      paddingBottom: 16,
      borderBottom: isDark
        ? "1px solid rgba(255,255,255,0.08)"
        : "1px solid rgba(0,0,0,0.08)",
      display: "flex",
      alignItems: "center",
      gap: 12,
    },
    modalTitle: {
      margin: 0,
      fontSize: 24,
      fontWeight: 800,
      background: isDark
        ? "linear-gradient(135deg, #fff 0%, #cbd5e1 100%)"
        : "linear-gradient(135deg, #0f172a 0%, #0f172a 100%)",
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: "transparent",
      backgroundClip: "text",
      letterSpacing: "-0.5px",
      display: "flex",
      alignItems: "center",
      gap: 10,
    },
    modalActions: {
      display: "flex",
      justifyContent: "flex-end",
      gap: 12,
      marginTop: 32,
      paddingTop: 20,
      borderTop: isDark
        ? "1px solid rgba(255,255,255,0.08)"
        : "1px solid rgba(0,0,0,0.08)",
    },
    primaryBtn: {
      background: "linear-gradient(135deg, #2e7cba 0%, #2e7cba 100%)",
      color: "#fff",
      padding: "12px 24px",
      borderRadius: 10,
      border: "none",
      display: "inline-flex",
      gap: 8,
      alignItems: "center",
      cursor: "pointer",
      fontWeight: 700,
      fontSize: 14,
      transition: "all 300ms cubic-bezier(0.34, 1.56, 0.64, 1)",
      textTransform: "uppercase",
      letterSpacing: "0.5px",
    },
    secondaryBtn: {
      background: isDark ? "rgba(30,41,59,0.8)" : "rgba(241,245,249,0.9)",
      color: isDark ? "#cbd5e1" : "#475569",
      padding: "12px 24px",
      borderRadius: 10,
      border: isDark
        ? "1.5px solid rgba(255,255,255,0.15)"
        : "1.5px solid rgba(0,0,0,0.12)",
      cursor: "pointer",
      fontWeight: 700,
      fontSize: 14,
      transition: "all 300ms ease",
      textTransform: "uppercase",
      letterSpacing: "0.5px",
    },
    formRow: {
      display: "flex",
      gap: 18,
      marginBottom: 18,
      flexWrap: "wrap",
    },
    formGroup: {
      flex: "1 1 calc(50% - 9px)",
      minWidth: 220,
      display: "flex",
      flexDirection: "column",
    },
    formLabel: {
      marginBottom: 10,
      fontSize: 12,
      fontWeight: 700,
      color: isDark ? "#94a3b8" : "#475569",
      textTransform: "uppercase",
      letterSpacing: "0.6px",
    },
    errorText: {
      color: "#ef4444",
      fontSize: 11,
      marginTop: 6,
      fontWeight: 600,
    },
    closeBtn: {
      position: "absolute",
      top: 14,
      right: language === "ar" ? "auto" : 14,
      left: language === "ar" ? 14 : "auto",
      width: 40,
      height: 40,
      borderRadius: 10,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      background: isDark ? "rgba(13,18,28,0.15)" : "rgba(255,255,255,0.92)",
      border: isDark
        ? "1px solid rgba(255,255,255,0.06)"
        : "1px solid rgba(15,23,42,0.06)",
      cursor: "pointer",
      transition: "all 200ms ease",
      zIndex: 20,
      color: isDark ? "#94a3b8" : "#475569",
    },
  };

  const styles = { ...defaultStyles, ...(stylesProp || {}) };
  const resolvedFonts = { body: "14px", subtitle: "1rem", ...fonts };
  const resolvedSpacing = {
    margin: "1.5rem",
    gap: "1rem",
    padding: "1rem",
    ...spacing,
  };

  const formattedDOB = (() => {
    const dob = selectedEmployee?.fullEmployeeData?.dateOfBirth;
    if (!dob) return "";
    try {
      const date = new Date(dob);
      return !isNaN(date.getTime()) ? date.toISOString().split("T")[0] : "";
    } catch {
      return "";
    }
  })();

  const [formData, setFormData] = useState({
    name:
      language === "ar"
        ? selectedEmployee?.arabicName?.trim() ||
          selectedEmployee?.name ||
          ""
        : selectedEmployee?.name || "",
    arabicName: selectedEmployee?.arabicName || "",
    email: selectedEmployee?.email || "",
    phone: selectedEmployee?.phone || "",
    department: selectedEmployee?.department || "",
    laborMode: selectedEmployee?.laborMode || "",
    company:
      selectedEmployee?.company ||
      clientName ||
      "",
    status: selectedEmployee?.status || "Active",
    salary: selectedEmployee?.salary || "",
    nationalId: selectedEmployee?.fullEmployeeData?.nationalId || "",
    address: selectedEmployee?.fullEmployeeData?.address || "",
    city: selectedEmployee?.fullEmployeeData?.city || "",
    country: selectedEmployee?.fullEmployeeData?.country || "Egypt",
    nationality: selectedEmployee?.fullEmployeeData?.nationality || "",
    gender: selectedEmployee?.fullEmployeeData?.gender || "",
    maritalStatus: selectedEmployee?.fullEmployeeData?.maritalStatus || "",
    dateOfBirth: formattedDOB,
    arabicAddress: selectedEmployee?.fullEmployeeData?.arabicAddress || "",
    transportationAllowances:
      selectedEmployee?.fullEmployeeData?.transportationAllowances || "",
    variableSalary:
      selectedEmployee?.fullEmployeeData?.variableSalary || "",
    workingCompany:
      selectedEmployee?.fullEmployeeData?.workingCompany ||
      clientName ||
      "",
  });

  const [formErrors, setFormErrors] = useState({});

  // ── Document upload state ──────────────────────────────────────────────────
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploadDocType, setUploadDocType] = useState("contract");
  const fileUploadRef = useRef(null);

  const handleFileSelect = (e) => {
    const selected = Array.from(e.target.files || []);
    addFiles(selected);
    e.target.value = "";
  };

  const addFiles = (incoming) => {
    setUploadedFiles((prev) => {
      const existingNames = new Set(prev.map((f) => f.name));
      return [...prev, ...incoming.filter((f) => !existingNames.has(f.name))];
    });
  };

  const handleRemoveFile = (fileName) => {
    setUploadedFiles((prev) => prev.filter((f) => f.name !== fileName));
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    addFiles(Array.from(e.dataTransfer.files || []));
  };

  const getFileIcon = (fileName) => {
    const ext = fileName.split(".").pop()?.toLowerCase();
    const colors = {
      pdf: "#ef4444",
      doc: "#2563eb",
      docx: "#2563eb",
      xls: "#16a34a",
      xlsx: "#16a34a",
      jpg: "#f59e0b",
      jpeg: "#f59e0b",
      png: "#8b5cf6",
    };
    return colors[ext] || "#64748b";
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };
  // ──────────────────────────────────────────────────────────────────────────

  // Dropdown option sets
  const statusOptionsEdit = [
    { key: "Active", label: text[language].statusActive },
    { key: "OnLeave", label: text[language].statusOnLeave },
    { key: "Inactive", label: text[language].statusInactive },
  ];
  const genderOptionsEdit = [
    { key: "Male", label: text[language].male },
    { key: "Female", label: text[language].female },
  ];
  const maritalOptionsEdit = [
    { key: "Single", label: text[language].single },
    { key: "Married", label: text[language].married },
    { key: "Divorced", label: text[language].divorced },
    { key: "Widowed", label: text[language].widowed },
  ];
  const laborModeOptionsEdit = [
    { key: "Full-time", label: "Full-time" },
    { key: "Part-time", label: "Part-time" },
    { key: "Contract", label: "Contract" },
    { key: "Temporary", label: "Temporary" },
    { key: "Freelance", label: "Freelance" },
  ];

  const [statusSearchTermEdit, setStatusSearchTermEdit] = useState(
    () =>
      statusOptionsEdit.find((s) => s.key === formData.status)?.label ||
      formData.status
  );
  const [genderSearchTermEdit, setGenderSearchTermEdit] = useState(
    () =>
      genderOptionsEdit.find((g) => g.key === formData.gender)?.label ||
      formData.gender
  );
  const [maritalSearchTermEdit, setMaritalSearchTermEdit] = useState(
    () =>
      maritalOptionsEdit.find((m) => m.key === formData.maritalStatus)
        ?.label || formData.maritalStatus
  );
  const [laborModeSearchTermEdit, setLaborModeSearchTermEdit] = useState(
    () =>
      laborModeOptionsEdit.find((l) => l.key === formData.laborMode)?.label ||
      formData.laborMode
  );

  const handleModalClick = (e) => {
    if (e.target === e.currentTarget) {
      setShowEditEmployee(false);
      setSelectedEmployee(null);
    }
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (formErrors[e.target.name]) {
      setFormErrors({ ...formErrors, [e.target.name]: "" });
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = text[language].nameRequired;
    if (!formData.email.trim()) {
      errors.email = text[language].emailRequired;
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = text[language].emailInvalid;
    }
    if (!formData.department.trim())
      errors.department = text[language].departmentRequired;
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log("[EditEmployeeModal] handleSubmit called");
    console.log("Form valid?", validateForm());
    if (!validateForm()) return;

    console.log("Selected employee:", selectedEmployee);
    console.log("Files to upload:", uploadedFiles);

    // Upload documents first if any
    let infinityCode = selectedEmployee?.infinityCode || selectedEmployee?.fullEmployeeData?.infinityCode;
    let fileName = uploadedFiles.length > 0 ? uploadedFiles[0].name : null;
    if (uploadedFiles.length > 0 && infinityCode) {
      for (const file of uploadedFiles) {
        try {
          console.log("Uploading file", file.name, "for", infinityCode);
          await uploadEmployeeDocument(infinityCode, file, uploadDocType);
          console.log("Upload success for", file.name);
        } catch (err) {
          console.error("Upload failed for", file.name, err);
          alert((language === "ar" ? "فشل رفع المستند: " : "Failed to upload document: ") + (err.message || file.name));
        }
      }
      setUploadedFiles([]); // Clear after upload
    } else {
      console.log("No files to upload or missing infinityCode");
    }

    // Continue with employee update
    await handleUpdateEmployee(formData, []);
  };

  const handleFormKeyDown = (e) => {
    if (e.key !== "Enter") return;
    const target = e.target;
    if (!target) return;
    const tag = (target.tagName || "").toLowerCase();
    if (tag === "button" || target.type === "submit") return;
    if (target.closest && target.closest("[data-reusable-dropdown]")) return;
    e.preventDefault();
  };

  // Shared styles
  const enhancedLabel = {
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    color: isDark ? "rgba(241, 245, 249, 0.9)" : "rgba(15, 23, 42, 0.8)",
    fontSize: resolvedFonts.body,
    fontWeight: "600",
    marginBottom: "0.625rem",
    letterSpacing: "0.01em",
  };

  const enhancedSectionTitle = {
    color: isDark ? "rgba(255, 255, 255, 0.95)" : "rgba(26, 26, 26, 0.9)",
    fontSize: resolvedFonts.subtitle,
    fontWeight: "700",
    margin: `${resolvedSpacing.margin} 0 ${screenSize === "xs" ? "1rem" : "1.25rem"} 0`,
    paddingBottom: "0.75rem",
    borderBottom: isDark
      ? "2px solid rgba(59, 130, 246, 0.3)"
      : "2px solid rgba(59, 130, 246, 0.2)",
    display: "flex",
    alignItems: "center",
    gap: "0.75rem",
  };

  // Guard after all hooks
  if (!selectedEmployee) return null;

  return (
    <div style={styles.modal} onClick={handleModalClick}>
      <div style={styles.modalContent} className="modal-content">

        {/* ── Modal Header ── */}
        <div style={styles.modalHeader}>
          <h2 style={styles.modalTitle}>
            <Edit
              size={screenSize === "xs" ? 18 : 20}
              style={{
                marginRight: language === "ar" ? 0 : "0.5rem",
                marginLeft: language === "ar" ? "0.5rem" : 0,
              }}
            />
            {text[language]?.editEmployee || "Edit Employee"} -{" "}
            {selectedEmployee?.name}
          </h2>
          <button
            style={styles.closeBtn}
            onClick={() => {
              setShowEditEmployee(false);
              setSelectedEmployee(null);
            }}
            title={language === "ar" ? "إغلاق" : "Close"}
          >
            <X size={18} />
          </button>
        </div>

        {/* ── Form ── */}
        <form onSubmit={handleSubmit} onKeyDown={handleFormKeyDown}>

          {/* ── Basic Information ── */}
          <div style={enhancedSectionTitle}>
            <User size={18} />
            {text[language].basicInformation}
          </div>

          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={enhancedLabel}>
                <User size={14} />
                {text[language].fullName} *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                style={unifiedControlStyle(theme, language === "ar")}
                placeholder={text[language].enterFullName}
              />
              {formErrors.name && (
                <span style={styles.errorText}>{formErrors.name}</span>
              )}
            </div>

            <div style={styles.formGroup}>
              <label style={enhancedLabel}>
                <User size={14} />
                {text[language].arabicName}
              </label>
              <input
                type="text"
                name="arabicName"
                value={formData.arabicName}
                onChange={handleInputChange}
                style={unifiedControlStyle(theme, language === "ar")}
                placeholder={
                  language === "ar" ? "أدخل الاسم بالعربي" : "Enter Arabic name"
                }
                dir="rtl"
              />
            </div>
          </div>

          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={enhancedLabel}>
                <Mail size={14} />
                {text[language].emailAddress} *
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                style={unifiedControlStyle(theme, language === "ar")}
                placeholder={text[language].enterEmail}
              />
              {formErrors.email && (
                <span style={styles.errorText}>{formErrors.email}</span>
              )}
            </div>

            <div style={styles.formGroup}>
              <label style={enhancedLabel}>
                <Phone size={14} />
                {text[language].phoneNumber}
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                style={unifiedControlStyle(theme, language === "ar")}
                placeholder={text[language].enterPhone}
              />
            </div>

            <div style={styles.formGroup}>
              <label style={enhancedLabel}>
                <User size={14} />
                {text[language].nationalId}
              </label>
              <input
                type="text"
                name="nationalId"
                value={formData.nationalId}
                onChange={handleInputChange}
                style={unifiedControlStyle(theme, language === "ar")}
                placeholder={text[language].enterNationalId}
              />
            </div>
          </div>

          {/* ── Work Information ── */}
          <div style={enhancedSectionTitle}>
            <Building size={18} />
            {text[language].workInformation}
          </div>

          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={enhancedLabel}>
                <Building size={14} />
                {text[language].department} *
              </label>
              <div style={{ position: "relative" }}>
                <ReusableDropdown
                  items={departments}
                  value={formData.department}
                  onChange={(val) =>
                    setFormData({ ...formData, department: val })
                  }
                  onSelect={(item) => {
                    const label =
                      typeof item === "string"
                        ? item
                        : item?.name || String(item);
                    setFormData({ ...formData, department: label });
                  }}
                  placeholder={text[language].selectDepartment}
                  getItemKey={(it) =>
                    typeof it === "string" ? it : it.id || String(it)
                  }
                  getItemLabel={(it) =>
                    typeof it === "string" ? it : it.name || String(it)
                  }
                  theme={theme}
                  language={language}
                  maxItems={10}
                  inputStyle={unifiedControlStyle(theme, language === "ar")}
                />
              </div>
              {formErrors.department && (
                <span style={styles.errorText}>{formErrors.department}</span>
              )}
            </div>

            <div style={styles.formGroup}>
              <label style={enhancedLabel}>
                <Building size={14} />
                {text[language].companyName}
              </label>
              <input
                type="text"
                name="company"
                value={formData.company}
                onChange={handleInputChange}
                style={unifiedControlStyle(theme, language === "ar")}
                placeholder={text[language].enterCompany}
              />
            </div>
          </div>

          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={enhancedLabel}>
                <CheckCircle size={14} />
                {text[language].statusLabel}
              </label>
              <div style={{ position: "relative" }}>
                <ReusableDropdown
                  items={statusOptionsEdit}
                  value={statusSearchTermEdit}
                  onChange={(val) => setStatusSearchTermEdit(val)}
                  onSelect={(item) => {
                    setFormData({ ...formData, status: item.key });
                    setStatusSearchTermEdit(item.label);
                  }}
                  placeholder={
                    text[language].selectStatus || text[language].statusLabel
                  }
                  getItemKey={(it) => it.key}
                  getItemLabel={(it) => it.label}
                  theme={theme}
                  language={language}
                  maxItems={10}
                  inputStyle={unifiedControlStyle(theme, language === "ar")}
                />
              </div>
            </div>

            <div style={styles.formGroup}>
              <label style={enhancedLabel}>
                <DollarSign size={14} />
                {text[language].salaryLabel}
              </label>
              <input
                type="number"
                name="salary"
                value={formData.salary}
                onChange={handleInputChange}
                style={unifiedControlStyle(theme, language === "ar")}
                placeholder={text[language].enterSalary}
                min="0"
              />
            </div>

            <div style={styles.formGroup}>
              <label style={enhancedLabel}>
                <User size={14} />
                {text[language].gender}
              </label>
              <div style={{ position: "relative" }}>
                <ReusableDropdown
                  items={genderOptionsEdit}
                  value={genderSearchTermEdit}
                  onChange={(val) => setGenderSearchTermEdit(val)}
                  onSelect={(item) => {
                    setFormData({ ...formData, gender: item.key });
                    setGenderSearchTermEdit(item.label);
                  }}
                  placeholder={text[language].selectGender}
                  getItemKey={(it) => it.key}
                  getItemLabel={(it) => it.label}
                  theme={theme}
                  language={language}
                  maxItems={10}
                  inputStyle={unifiedControlStyle(theme, language === "ar")}
                />
              </div>
            </div>

            <div style={styles.formGroup}>
              <label style={enhancedLabel}>
                <Award size={14} />
                {language === "ar" ? "نمط العمل" : "Labor Mode"}
              </label>
              <div style={{ position: "relative" }}>
                <ReusableDropdown
                  items={laborModeOptionsEdit}
                  value={laborModeSearchTermEdit}
                  onChange={(val) => setLaborModeSearchTermEdit(val)}
                  onSelect={(item) => {
                    setFormData({ ...formData, laborMode: item.key });
                    setLaborModeSearchTermEdit(item.label);
                  }}
                  placeholder={
                    language === "ar" ? "اختر نمط العمل" : "Select Labor Mode"
                  }
                  getItemKey={(it) => it.key}
                  getItemLabel={(it) => it.label}
                  theme={theme}
                  language={language}
                  maxItems={10}
                  inputStyle={unifiedControlStyle(theme, language === "ar")}
                />
              </div>
            </div>
          </div>

          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={enhancedLabel}>
                <DollarSign size={14} />
                {language === "ar"
                  ? "بدل المواصلات"
                  : "Transportation Allowances"}
              </label>
              <input
                type="number"
                name="transportationAllowances"
                value={formData.transportationAllowances}
                onChange={handleInputChange}
                style={unifiedControlStyle(theme, language === "ar")}
                placeholder={
                  language === "ar"
                    ? "أدخل بدل المواصلات"
                    : "Enter transportation allowances"
                }
                min="0"
              />
            </div>

            <div style={styles.formGroup}>
              <label style={enhancedLabel}>
                <DollarSign size={14} />
                {language === "ar" ? "الراتب المتغير" : "Variable Salary"}
              </label>
              <input
                type="number"
                name="variableSalary"
                value={formData.variableSalary}
                onChange={handleInputChange}
                style={unifiedControlStyle(theme, language === "ar")}
                placeholder={
                  language === "ar"
                    ? "أدخل الراتب المتغير"
                    : "Enter variable salary"
                }
                min="0"
              />
            </div>
          </div>

          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={enhancedLabel}>
                <Building size={14} />
                {language === "ar" ? "الشركة العاملة" : "Working Company"}
              </label>
              <input
                type="text"
                name="workingCompany"
                value={formData.workingCompany}
                onChange={handleInputChange}
                style={unifiedControlStyle(theme, language === "ar")}
                placeholder={
                  language === "ar"
                    ? "أدخل الشركة العاملة"
                    : "Enter working company"
                }
              />
            </div>

            <div style={styles.formGroup}>
              <label style={enhancedLabel}>
                <MapPin size={14} />
                {language === "ar" ? "العنوان بالعربية" : "Arabic Address"}
              </label>
              <input
                type="text"
                name="arabicAddress"
                value={formData.arabicAddress}
                onChange={handleInputChange}
                style={unifiedControlStyle(theme, language === "ar")}
                placeholder="أدخل العنوان بالعربية"
              />
            </div>
          </div>

          {/* ── Personal Information ── */}
          <div style={enhancedSectionTitle}>
            <Home size={18} />
            {text[language].personalInformation}
          </div>

          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={enhancedLabel}>
                <Calendar size={14} />
                {text[language].dateOfBirth}
              </label>
              <input
                type="date"
                name="dateOfBirth"
                value={formData.dateOfBirth}
                onChange={handleInputChange}
                style={unifiedControlStyle(theme, language === "ar")}
              />
            </div>

            <div style={styles.formGroup}>
              <label style={enhancedLabel}>
                <Heart size={14} />
                {text[language].maritalStatus}
              </label>
              <div style={{ position: "relative" }}>
                <ReusableDropdown
                  items={maritalOptionsEdit}
                  value={maritalSearchTermEdit}
                  onChange={(val) => setMaritalSearchTermEdit(val)}
                  onSelect={(item) => {
                    setFormData({ ...formData, maritalStatus: item.key });
                    setMaritalSearchTermEdit(item.label);
                  }}
                  placeholder={text[language].selectMaritalStatus}
                  getItemKey={(it) => it.key}
                  getItemLabel={(it) => it.label}
                  theme={theme}
                  language={language}
                  maxItems={10}
                  inputStyle={unifiedControlStyle(theme, language === "ar")}
                />
              </div>
            </div>
          </div>

          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={enhancedLabel}>
                <Flag size={14} />
                {text[language].nationality}
              </label>
              <input
                type="text"
                name="nationality"
                value={formData.nationality}
                onChange={handleInputChange}
                style={unifiedControlStyle(theme, language === "ar")}
                placeholder={text[language].enterNationality}
              />
            </div>

            <div style={styles.formGroup}>
              <label style={enhancedLabel}>
                <MapPin size={14} />
                {text[language].city}
              </label>
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleInputChange}
                style={unifiedControlStyle(theme, language === "ar")}
                placeholder={text[language].enterCity}
              />
            </div>
          </div>

          <div style={styles.formGroup}>
            <label style={enhancedLabel}>
              <MapPin size={14} />
              {text[language].address}
            </label>
            <input
              type="text"
              name="address"
              value={formData.address}
              onChange={handleInputChange}
              style={unifiedControlStyle(theme, language === "ar")}
              placeholder={text[language].enterAddress}
            />
          </div>

          {/* ── Documents Upload Section ── */}
          <div style={enhancedSectionTitle}>
            <pdf size={18} />
            {language === "ar" ? "المستندات" : "Documents"}
          </div>

          {/* Document type selector */}
          <div style={{ marginBottom: 16 }}>
            <label style={enhancedLabel}>
              {language === "ar" ? "نوع المستند" : "Document Type"}
            </label>
            <ReusableDropdown
              items={[
                { value: "contract",    label: language === "ar" ? "عقد" : "Contract" },
                { value: "payslip",     label: language === "ar" ? "كشف راتب" : "Payslip" },
                { value: "certificate", label: language === "ar" ? "شهادة" : "Certificate" },
                { value: "policy",      label: language === "ar" ? "سياسة" : "Policy" },
              ]}
              value={[
                { value: "contract",    label: language === "ar" ? "عقد" : "Contract" },
                { value: "payslip",     label: language === "ar" ? "كشف راتب" : "Payslip" },
                { value: "certificate", label: language === "ar" ? "شهادة" : "Certificate" },
                { value: "policy",      label: language === "ar" ? "سياسة" : "Policy" },
              ].find((i) => i.value === uploadDocType)?.label || ""}
              onSelect={(item) => setUploadDocType(item.value)}
              onChange={() => {}}
              placeholder={language === "ar" ? "اختر نوع المستند" : "Select document type"}
              theme={theme}
              language={language}
              getItemLabel={(item) => item.label}
              getItemKey={(item) => item.value}
            />
          </div>

          {/* Hidden file input */}
          <input
            ref={fileUploadRef}
            type="file"
            multiple
            accept=".pdf,.jpg,.jpeg,.png,.docx,.doc,.xlsx,.xls"
            style={{ display: "none" }}
            onChange={handleFileSelect}
          />

          {/* Drop zone */}
          <div
            onClick={() => fileUploadRef.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              setIsDragOver(true);
            }}
            onDragLeave={() => setIsDragOver(false)}
            onDrop={handleDrop}
            style={{
              border: `2px dashed ${
                isDragOver
                  ? "#2e7cba"
                  : isDark
                  ? "rgba(255,255,255,0.15)"
                  : "rgba(15,23,42,0.15)"
              }`,
              borderRadius: 12,
              padding: "24px 20px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 10,
              cursor: "pointer",
              transition: "border-color 200ms, background 200ms",
              background: isDragOver
                ? isDark
                  ? "rgba(46,124,186,0.08)"
                  : "rgba(46,124,186,0.04)"
                : "transparent",
              marginBottom: uploadedFiles.length > 0 ? 14 : 0,
              userSelect: "none",
            }}
          >
            <div
              style={{
                width: 44,
                height: 44,
                borderRadius: 10,
                background: isDark
                  ? "rgba(46,124,186,0.15)"
                  : "rgba(46,124,186,0.08)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Upload
                size={20}
                style={{ color: "#2e7cba" }}
              />
            </div>
            <div style={{ textAlign: "center" }}>
              <p
                style={{
                  margin: 0,
                  fontSize: 14,
                  fontWeight: 600,
                  color: isDark ? "#cbd5e1" : "#374151",
                }}
              >
                {language === "ar"
                  ? "اضغط لاختيار ملفات أو اسحبها هنا"
                  : "Click to select files or drag & drop"}
              </p>
              <p
                style={{
                  margin: "4px 0 0",
                  fontSize: 12,
                  color: isDark ? "#64748b" : "#94a3b8",
                }}
              >
                {language === "ar"
                  ? "PDF، JPG، PNG، DOCX، XLSX"
                  : "PDF, JPG, PNG, DOCX, XLSX"}
              </p>
            </div>
          </div>

          {/* Attached file list */}
          {uploadedFiles.length > 0 && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 8,
                marginBottom: 4,
              }}
            >
              {/* Counter badge */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  marginBottom: 4,
                }}
              >
                <pdf
                  size={13}
                  style={{ color: isDark ? "#94a3b8" : "#64748b" }}
                />
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: isDark ? "#94a3b8" : "#64748b",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  {uploadedFiles.length}{" "}
                  {language === "ar"
                    ? uploadedFiles.length === 1
                      ? "ملف مرفق"
                      : "ملفات مرفقة"
                    : uploadedFiles.length === 1
                    ? "file attached"
                    : "files attached"}
                </span>
              </div>

              {uploadedFiles.map((file) => (
                <div
                  key={file.name}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "10px 14px",
                    borderRadius: 10,
                    background: isDark
                      ? "rgba(255,255,255,0.04)"
                      : "rgba(0,0,0,0.03)",
                    border: isDark
                      ? "1px solid rgba(255,255,255,0.07)"
                      : "1px solid rgba(0,0,0,0.07)",
                    transition: "background 150ms",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      minWidth: 0,
                    }}
                  >
                    {/* File type dot */}
                    <div
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius: 7,
                        background: `${getFileIcon(file.name)}18`,
                        border: `1px solid ${getFileIcon(file.name)}33`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <FileText
                        size={14}
                        style={{ color: getFileIcon(file.name) }}
                      />
                    </div>

                    <div style={{ minWidth: 0 }}>
                      <p
                        style={{
                          margin: 0,
                          fontSize: 13,
                          fontWeight: 600,
                          color: isDark ? "#e2e8f0" : "#1e293b",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: "38ch",
                        }}
                      >
                        {file.name}
                      </p>
                      <p
                        style={{
                          margin: "2px 0 0",
                          fontSize: 11,
                          color: isDark ? "#64748b" : "#94a3b8",
                        }}
                      >
                        {formatFileSize(file.size)}
                      </p>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => handleRemoveFile(file.name)}
                    title={language === "ar" ? "إزالة" : "Remove"}
                    style={{
                      background: "transparent",
                      border: "none",
                      cursor: "pointer",
                      padding: 6,
                      borderRadius: 6,
                      color: "#ef4444",
                      display: "flex",
                      alignItems: "center",
                      flexShrink: 0,
                      transition: "background 150ms",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.background = "rgba(239,68,68,0.1)")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.background = "transparent")
                    }
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* ── Modal Actions ── */}
          <div style={styles.modalActions}>
            <button
              type="submit"
              style={{
                ...styles.primaryBtn,
                opacity: loading ? 0.7 : 1,
                cursor: loading ? "not-allowed" : "pointer",
              }}
              disabled={loading}
              onMouseEnter={handlePrimaryBtnMouseEnter}
              onMouseLeave={handlePrimaryBtnMouseLeave}
            >
              <Save size={screenSize === "xs" ? 14 : 16} />
              {loading ? text[language].updating : text[language].update}
              {!loading && uploadedFiles.length > 0 && (
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 18,
                    height: 18,
                    borderRadius: "50%",
                    background: "rgba(255,255,255,0.25)",
                    fontSize: 10,
                    fontWeight: 800,
                    marginLeft: 2,
                  }}
                >
                  +{uploadedFiles.length}
                </span>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditEmployeeModal;